import { useMemo, useRef, useState } from "react";
import { useLive } from "../../state/use-live.js";
import { storage } from "../../state/storage.js";
import { useUiStore } from "../../state/ui-store.js";
import { exportConversation, importConversation } from "../../state/conversation-actions.js";
import { gameModules } from "../../state/game-modules.js";
import { startGameSession } from "../../state/game-session.js";
import { CharacterTemplateWizard } from "./CharacterTemplateWizard.js";
import {
  Archive,
  Compass,
  Dices,
  Download,
  Gamepad2,
  Key,
  MessageCircle,
  Pin,
  PinOff,
  Map,
  ShieldCheck,
  Sparkles,
  Store,
  Trash2,
  VenetianMask,
  Vote,
  X,
} from "lucide-react";
import { useT } from "../../i18n/use-t.js";
import { createChat, createMessage, type Character, type Chat, type GameModule } from "@roqeet/core";
import { useEscapeToClose } from "../use-escape-to-close.js";

/**
 * 대화 목록(결정 #13 반응형 읽기). 새 대화 생성/열기 + 목록 관리(고정/아카이브/
 * 삭제, 결정 #16 조직화) + 제목 검색. 고정 대화는 맨 위로 정렬된다(storage.liveChats).
 */
export function ChatsView() {
  const { t } = useT();
  const chats = useLive<Chat[]>(() => storage.liveChats(), []);
  const setActiveChatId = useUiStore((s) => s.setActiveChatId);
  const [query, setQuery] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [characterTemplateOpen, setCharacterTemplateOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // 제목 검색(결정 #11 콘텐츠 탐색). 클라이언트 측 필터.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return chats ?? [];
    return (chats ?? []).filter((c) => c.title.toLowerCase().includes(q));
  }, [chats, query]);

  // 대화 번들 가져오기(결정 #6). ID 재매핑은 core가 처리.
  const onImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setNotice(null);
    try {
      const id = await importConversation(file);
      setActiveChatId(id);
    } catch (err) {
      setNotice(t("chats.importFailed", { error: err instanceof Error ? err.message : String(err) }));
    }
  };

  /**
   * 선택한 게임 모듈로 새 세션을 만들고 채팅방을 연다(결정 #25/#28). 세션 생성
   * 로직은 GamesView와 공유하는 startGameSession 액션에 위임한다(중복 제거).
   */
  const createSessionFor = async (module: GameModule) => {
    setPickerOpen(false);
    const chatId = await startGameSession(module, (k, ...args) => t(k as any, ...args));
    setActiveChatId(chatId);
  };

  const startCharacterChat = async (character: Character) => {
    const chat = createChat({ title: t("char.chatTitle", { name: character.name }), characterId: character.id });
    chat.lastMessageAt = Date.now();
    await storage.chats.put(chat);

    const text = character.firstMessage || character.alternateGreetings[0] || "";
    if (text) {
      chat.messageCount = 1;
      await storage.chats.put(chat);
      const greeting = createMessage({
        chatId: chat.id,
        senderType: "character",
        characterId: character.id,
        senderName: character.name,
        content: text,
        order: 0,
        isGreeting: true,
      });
      await storage.putMessage(greeting);
    }
    setActiveChatId(chat.id);
  };

  const togglePin = async (chat: Chat) => {
    await storage.chats.put({ ...chat, pinned: !chat.pinned });
  };

  const archive = async (chat: Chat) => {
    await storage.chats.put({ ...chat, archived: true });
  };

  const remove = async (chat: Chat) => {
    if (!confirm(t("chats.deleteConfirm", { title: chat.title }))) return;
    await storage.chats.delete(chat.id);
  };

  return (
    <div className="flex h-full flex-col">
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={onImport}
      />
      <div className="flex items-center justify-between gap-2 border-b border-line p-3">
        <span className="text-sm text-ink-soft">
          {chats ? t("chats.count", { count: chats.length }) : t("common.loading")}
        </span>
        <div className="flex gap-2">
          <button
            className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
            onClick={() => fileRef.current?.click()}
            title={t("chats.importTitle")}
          >
            {t("common.import")}
          </button>
          <button
            className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft"
            onClick={() => setPickerOpen(true)}
          >
            {t("chats.newChat")}
          </button>
        </div>
      </div>

      {/* 게임 모듈 선택 모달(결정 #24/#25): 무엇을 플레이할지 고른다. */}
      {pickerOpen && (
        <ModulePicker
          onPick={createSessionFor}
          onCreateRoleplayTemplate={() => {
            setPickerOpen(false);
            setCharacterTemplateOpen(true);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {characterTemplateOpen && (
        <CharacterTemplateWizard
          onClose={() => setCharacterTemplateOpen(false)}
          onCreated={async (character, shouldStartChat) => {
            if (shouldStartChat) await startCharacterChat(character);
          }}
        />
      )}

      {notice && (
        <div className="flex items-start justify-between gap-2 border-b border-line bg-bg-soft px-3 py-2 text-xs text-ink-soft">
          <span className="min-w-0 break-words">{notice}</span>
          <button
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-faint hover:bg-bg-raised hover:text-ink"
            onClick={() => setNotice(null)}
            aria-label={t("misc.close")}
            title={t("misc.close")}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* 제목 검색(결정 #11 콘텐츠 탐색). */}
      {(chats?.length ?? 0) > 0 && (
        <div className="border-b border-line p-3">
          <input
            className="w-full rounded-lg border border-line bg-bg-soft px-3 py-2 text-sm text-ink outline-none focus:border-accent"
            placeholder={t("chats.searchPlaceholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}

      <ul className="min-h-0 flex-1 overflow-y-auto p-2 scroll-touch">
        {chats?.length === 0 && (
          <li className="px-3 py-8 text-center text-sm text-ink-faint">
            {t("chats.empty")}
          </li>
        )}
        {chats && chats.length > 0 && filtered.length === 0 && (
          <li className="px-3 py-8 text-center text-sm text-ink-faint">
            {t("chats.noResults")}
          </li>
        )}
        {filtered.map((chat) => (
          <li key={chat.id} className="group relative">
            <button
              className="mb-1 flex w-full flex-col items-start rounded-lg px-3 py-2 pr-24 text-left hover:bg-bg-raised"
              onClick={() => setActiveChatId(chat.id)}
            >
              <span className="flex items-center gap-1.5 text-sm text-ink">
                {chat.pinned && (
                  <Pin size={14} className="shrink-0 text-accent-soft" aria-label={t("chats.pinned")} />
                )}
                <span className="truncate">{chat.title}</span>
              </span>
              <span className="text-xs text-ink-faint">{t("chats.messageCount", { count: chat.messageCount ?? 0 })}</span>
            </button>

            {/* 호버/포커스 시 관리 액션 노출 */}
            <div className="action-reveal absolute right-2 top-1.5 z-10 flex items-center gap-1">
              <IconBtn label={chat.pinned ? t("chats.unpin") : t("chats.pin")} onClick={() => togglePin(chat)}>
                {chat.pinned ? <PinOff size={15} aria-hidden="true" /> : <Pin size={15} aria-hidden="true" />}
              </IconBtn>
              <IconBtn label={t("common.export")} onClick={() => exportConversation(chat.id)}>
                <Download size={15} aria-hidden="true" />
              </IconBtn>
              <IconBtn label={t("chats.archive")} onClick={() => archive(chat)}>
                <Archive size={15} aria-hidden="true" />
              </IconBtn>
              <IconBtn label={t("common.delete")} onClick={() => remove(chat)} danger>
                <Trash2 size={15} aria-hidden="true" />
              </IconBtn>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function IconBtn({
  label,
  onClick,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      aria-label={label}
      title={label}
      className={[
        "flex h-7 w-7 items-center justify-center rounded-md border border-line bg-bg/90 transition-colors hover:bg-bg-raised",
        danger ? "text-red-400 hover:border-red-300/50" : "text-ink-soft hover:text-ink",
      ].join(" ")}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}

/**
 * 게임 모듈 선택 모달(결정 #24/#25). 설치된 모듈을 카드로 보여주고, 고르면 그
 * 모듈의 세션을 만든다. 데이터형 모듈(#29)이 추가되면 같은 레지스트리에 자동 노출된다.
 */
function ModulePicker({
  onPick,
  onCreateRoleplayTemplate,
  onClose,
}: {
  onPick: (module: GameModule) => void | Promise<void>;
  onCreateRoleplayTemplate: () => void;
  onClose: () => void;
}) {
  const { t } = useT();
  const modules = gameModules.list();
  useEscapeToClose(onClose);
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex max-h-screen max-h-[100dvh] w-full max-w-md flex-col overflow-hidden rounded-t-2xl border border-line bg-bg sm:max-h-[calc(100vh-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold text-ink">{t("chats.whatToPlay")}</h2>
          <button
            aria-label={t("chat.close")}
            className="flex h-8 w-8 items-center justify-center rounded-md text-ink-faint hover:bg-bg-raised hover:text-ink"
            onClick={onClose}
          >
            <X size={17} aria-hidden="true" />
          </button>
        </div>
        <ul className="min-h-0 flex-1 overflow-y-auto p-2 scroll-touch">
          <li className="mb-2">
            <button
              className="flex w-full items-start gap-3 rounded-xl border border-accent/35 bg-accent/10 px-3 py-3 text-left hover:border-accent hover:bg-accent/15"
              onClick={onCreateRoleplayTemplate}
            >
              <span className="mt-0.5" aria-hidden>
                <Sparkles className="h-5 w-5 text-accent" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-ink">롤플레잉 캐릭터 템플릿</span>
                <span className="mt-0.5 block text-xs leading-5 text-ink-faint">
                  이름, 성격, 관계, 첫 장면만 채워 캐릭터와의 대화를 바로 시작합니다.
                </span>
              </span>
            </button>
          </li>
          {modules.map((m) => (
            <li key={m.manifest.id}>
              <button
                className="flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left hover:bg-bg-raised"
                onClick={() => onPick(m)}
              >
                <span className="mt-0.5" aria-hidden>
                  {moduleIcon(m)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-ink">
                    {m.manifest.titleKey ? t(m.manifest.titleKey as any) : m.manifest.title}
                    {m.manifest.kind === "data" && (
                      <span className="rounded bg-bg-soft px-1 py-0.5 text-[10px] text-ink-faint">
                        {t("chats.custom")}
                      </span>
                    )}
                  </span>
                  {m.manifest.description && (
                    <span className="mt-0.5 block text-xs text-ink-faint">
                      {m.manifest.descriptionKey ? t(m.manifest.descriptionKey as any) : m.manifest.description}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** 모듈 아이콘 힌트를 SVG 아이콘으로 매핑(결정 #25). */
function moduleIcon(module: GameModule, className = "w-5 h-5 text-accent"): React.ReactNode {
  switch (module.manifest.icon) {
    case "compass": return <Compass className={className} />;
    case "chat": return <MessageCircle className={className} />;
    case "dice": return <Dices className={className} />;
    case "mask": return <VenetianMask className={className} />;
    case "key": return <Key className={className} />;
    case "vote": return <Vote className={className} />;
    case "map": return <Map className={className} />;
    case "sparkles": return <Sparkles className={className} />;
    case "store": return <Store className={className} />;
    case "shield": return <ShieldCheck className={className} />;
    default: return <Gamepad2 className={className} />;
  }
}
